// THE FIRST SIXTY SECONDS.
//
// The design says the output tray is not empty on first launch, and it says why:
// the best thing this app has is the page that comes out of it, and every
// casual persona met an empty machine instead. What actually shipped was a
// brand-new unit whose out tray read "OUTBOX EMPTY. NOTHING HAS LEFT THIS
// BUILDING." — a first screen whose most prominent statement is that the app has
// never done anything.
//
// So the machine prints its own test page, which is what every fax machine and
// every printer ever built did the first time it was switched on. It is not a
// fake fax and it is not sample data pretending to be the user's: the machine
// really did render it, through the same pipeline as everything else, and it
// says so on the page. That is why it can sit in the outbox marked SENT without
// lying — and why it does not need a DemoBanner over the top of it.
//
// It happens ONCE, guarded by a pref rather than by "the outbox is empty", so
// deleting it is permanent. A machine that reprints its test page every time you
// clear the tray is a machine you cannot turn off.

import { prefs } from '../core/storage.js';
import { isDemo } from '../core/demo.js';
import { faxs, addFax, saveFax, storeReady } from '../store.js';
import { ensureMachine, machine } from './machine.js';
import { renderPage, thumbnail } from './render.js';
import { REPLY_URL } from './reply.js';

const FLAG = 'jtf.testPagePrinted';

/** Fixed, so the test page looks the same on every machine ever sold. */
const SEED = 19_871_101;

let running = null;

export function ensureTestPage() {
  if (!running) running = print().catch(() => null);
  return running;
}

async function print() {
  // /demo already has a full tray, and it shares an origin — and therefore a
  // localStorage — with /app. Setting the flag here would mean that looking at
  // the demo once silently robbed the real machine of its first page.
  if (isDemo.value) return null;

  await storeReady;
  await ensureMachine();

  // Never twice, and never over the top of somebody's own outbox.
  if (prefs[FLAG]) return null;
  if (faxs.value.length) { prefs[FLAG] = true; return null; }

  prefs[FLAG] = true;

  const row = addFax({
    kind: 'memo',
    toName: 'THE NEW OWNER',
    fromName: 'JTF-1 SELF TEST',
    subject: 'TEST PAGE',
    // One line, because one line is all the cover band prints — and this line
    // is the only place the app states the whole deal before you use it.
    memo: 'This page came out of your machine. Nothing was sent.',
    source: '',
    seed: SEED,
    // MINT is the legibility floor: no skew, no dropouts, no streaks. The first
    // page anyone sees has to be readable, and the wrecking is the toy.
    condition: 'mint',
    pages: 1,
    status: 'sent',
    sentAt: new Date().toISOString(),
  });

  // Render it for real. A stored thumbnail of a page that was never rendered is
  // exactly the "sample data that looks like your own" this app refuses to ship.
  try {
    const { canvas, blank } = await renderPage({
      source: null, fax: row, machine: machine.value, replyUrl: REPLY_URL,
    });
    if (canvas && !blank) saveFax({ ...row, thumb: thumbnail(canvas) });
  } catch {
    // A machine that cannot render its own test page still has to boot.
  }

  return row;
}
